/**
 * <market-suggestion>
 *
 * Offers a shopper who is browsing from country X a link to the market that
 * actually serves country X. Rendered by sections/shop-redirect.liquid on
 * every page; shows at most once per session.
 *
 * Two independent axes decide whether anything appears, and both matter:
 *
 *   SELECTION   keys off the DETECTED country -- the shopper's own location,
 *               read from /browsing_context_suggestions.
 *   SUPPRESSION compares the RENDERED market's country (server-side,
 *               localization.country.iso_code, on the element as
 *               data-market-country) with that same detected country. Equal,
 *               case-insensitively, means the shopper is already on the right
 *               market and nothing is shown.
 *
 * Conflating the two -- e.g. matching blocks against the rendered market --
 * would show a German shopper on the German market a link to the German
 * market, and would never show the popup to anybody it is meant for.
 *
 * Everything lives on the class. The live version declared five top-level
 * `const`s in global scope, one of them literally named `settings`; a single
 * collision with another inline script was a SyntaxError that killed both.
 */
class MarketSuggestion extends HTMLElement {
  /**
   * sessionStorage key, verbatim from the live theme. Changing it would
   * un-dismiss the popup for every shopper mid-session on deploy day.
   * The flag is global, not per country: dismissing it once dismisses it
   * for the whole session, whatever market the shopper moves to.
   */
  static DISMISSED_KEY = 'shop-redirect-popup-closed';

  static OPEN_CLASS = 'market-suggestion--open';

  static CLOSE_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="20" height="20" ' +
    'fill="currentColor" focusable="false" aria-hidden="true">' +
    '<path d="M176.662 817.173c-8.19 8.471-7.96 21.977 0.51 30.165 8.472 8.19 21.978 7.96 30.166-0.51l618.667-640' +
    'c8.189-8.472 7.96-21.978-0.511-30.166-8.471-8.19-21.977-7.96-30.166 0.51l-618.666 640z"/>' +
    '<path d="M795.328 846.827c8.19 8.471 21.695 8.7 30.166 0.511 8.471-8.188 8.7-21.694 0.511-30.165l-618.667-640' +
    'c-8.188-8.471-21.694-8.7-30.165-0.511-8.471 8.188-8.7 21.694-0.511 30.165l618.666 640z"/>' +
    '</svg>';

  #suggestion = null;
  #openedBy = null;
  #keydownHandler = null;
  #started = false;

  connectedCallback() {
    if (this.#started) return;
    this.#started = true;
    this.#start();
  }

  disconnectedCallback() {
    this.#releaseKeyboard();
  }

  // --- lifecycle -----------------------------------------------------------

  async #start() {
    // Dismissal is consulted BEFORE the network call. The live version fetched
    // first and checked afterwards, which cost one request per pageview,
    // site-wide, for a popup that could never be shown again this session.
    if (this.#isDismissed()) return;

    const redirects = this.#readSettings();
    if (!redirects.length) return;

    const detectedCountry = await this.#detectCountry();
    if (!detectedCountry) return;

    const marketCountry = this.dataset.marketCountry;

    // Selection is by detected country; suppression compares the rendered
    // market against that same detected country. Both clauses are required.
    const suggestion = redirects.find(
      (redirect) =>
        redirect.country?.toLowerCase() === detectedCountry?.toLowerCase() &&
        marketCountry?.toLowerCase() !== detectedCountry?.toLowerCase()
    );

    if (!suggestion || !suggestion.enabled) return;

    this.#suggestion = suggestion;
    this.#render(suggestion);
    this.open();
  }

  #readSettings() {
    const node = document.querySelector('[data-market-suggestion-settings]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed?.redirects) ? parsed.redirects : [];
    } catch (error) {
      return [];
    }
  }

  async #detectCountry() {
    const root = window.Shopify?.routes?.root ?? '/';
    try {
      const response = await fetch(`${root}browsing_context_suggestions`);
      const payload = await response.json();
      return payload?.detected_values?.country?.handle;
    } catch (error) {
      return null;
    }
  }

  // --- rendering -----------------------------------------------------------

  #render(suggestion) {
    const panel = document.createElement('div');
    panel.className = 'market-suggestion__panel';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'market-suggestion__close';
    closeButton.setAttribute('aria-label', this.dataset.closeLabel || 'Close');
    closeButton.innerHTML = MarketSuggestion.CLOSE_ICON;
    // Bound to THIS popup and attached inside the render path. The live
    // version used an unscoped document.querySelector('.close-btn') and
    // attached the listener outside the render guard, so it bound to whatever
    // else on the page happened to carry that class and ran even when nothing
    // had been rendered.
    closeButton.addEventListener('click', () => this.close());
    panel.appendChild(closeButton);

    if (suggestion.image) {
      const image = document.createElement('img');
      image.className = 'market-suggestion__image';
      image.src = suggestion.image;
      image.alt = suggestion.image_alt || '';
      image.width = 80;
      image.height = 80;
      image.loading = 'lazy';
      panel.appendChild(image);
    }

    if (suggestion.heading) {
      const heading = document.createElement('h2');
      heading.className = 'market-suggestion__heading';
      heading.id = 'MarketSuggestionHeading';
      // textContent, not interpolation into an HTML string: these are plain
      // text settings, and building them into markup meant an apostrophe or
      // ampersand in a merchant's heading could break the popup. It is also
      // what removes the stray `}` the live template emitted after the
      // subheading -- latent only because all twelve subheadings are empty.
      heading.textContent = suggestion.heading;
      panel.appendChild(heading);
      this.setAttribute('aria-labelledby', heading.id);
    } else if (suggestion.btn_label) {
      this.setAttribute('aria-label', suggestion.btn_label);
    }

    if (suggestion.subheading) {
      // h4, matching live. It skips a level under the h2, but the bar for this
      // piece is 1:1 with live and the element is never emitted in practice --
      // all twelve configured subheadings are empty.
      const subheading = document.createElement('h4');
      subheading.className = 'market-suggestion__subheading';
      subheading.textContent = suggestion.subheading;
      panel.appendChild(subheading);
    }

    if (suggestion.content) {
      const content = document.createElement('div');
      content.className = 'market-suggestion__content';
      // richtext setting: markup is the point of the field.
      content.innerHTML = suggestion.content;
      panel.appendChild(content);
    }

    if (suggestion.btn_label && suggestion.btn_url) {
      const link = document.createElement('a');
      link.className = 'button market-suggestion__button';
      link.textContent = suggestion.btn_label;
      link.href = this.#resolveHref(suggestion.btn_url) ?? suggestion.btn_url;
      if (suggestion.open_in_new_tab) {
        link.target = '_blank';
        link.rel = 'noopener';
        const newWindowLabel = this.dataset.newWindowLabel;
        if (newWindowLabel) {
          link.setAttribute('aria-label', `${suggestion.btn_label} ${newWindowLabel}`);
        }
      }
      panel.appendChild(link);
    }

    this.replaceChildren(panel);
  }

  /**
   * Where the CTA should actually land, in order of preference.
   *
   * 1. The equivalent page on the target market. Shopify emits a
   *    <link rel="alternate" hreflang> for every market the current URL
   *    exists in, already pointing at the translated path -- so a shopper
   *    reading a product page is offered that product, not the home page.
   *
   * 2. Product pages only, when (1) found nothing (separate stores have no
   *    shared hreflang set): the target's search page, by SKU. A cross-origin
   *    navigation needs no CORS, so this works store to store.
   *
   * The square brackets in `options[unavailable_products]` are intentionally
   * NOT percent-encoded -- that is the parameter name Shopify's search
   * endpoint expects, and encoding them makes the filter silently inert.
   */
  #resolveHref(baseUrl) {
    let resolved = null;

    try {
      const baseAbs = /^https?:\/\//i.test(baseUrl) ? baseUrl : new URL(baseUrl, window.location.origin).href;
      const baseTrim = baseAbs.replace(/\/$/, '');
      const alternates = document.querySelectorAll('link[rel="alternate"][hreflang][href]');
      for (const link of alternates) {
        const href = new URL(link.getAttribute('href'), window.location.origin).href;
        if (href === baseTrim || href.startsWith(`${baseTrim}/`)) {
          resolved = href;
          break;
        }
      }
    } catch (error) {
      resolved = null;
    }

    if (!resolved) {
      const productSku = this.#currentProductSku();
      if (productSku) {
        resolved = `${baseUrl.replace(/\/$/, '')}/search?q=${encodeURIComponent(
          productSku
        )}&type=product&options[unavailable_products]=last`;
      }
    }

    return resolved;
  }

  #currentProductSku() {
    const node = document.querySelector('[data-current-product-sku]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || 'null');
    } catch (error) {
      return null;
    }
  }

  // --- open / close --------------------------------------------------------

  open() {
    if (!this.#suggestion || this.classList.contains(MarketSuggestion.OPEN_CLASS)) return;

    this.#openedBy = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.classList.add(MarketSuggestion.OPEN_CLASS);

    this.#keydownHandler = this.#onKeydown.bind(this);
    document.addEventListener('keydown', this.#keydownHandler);

    this.focus();
  }

  close() {
    const wasOpen = this.classList.contains(MarketSuggestion.OPEN_CLASS);
    this.classList.remove(MarketSuggestion.OPEN_CLASS);
    this.#releaseKeyboard();

    try {
      sessionStorage.setItem(MarketSuggestion.DISMISSED_KEY, 'true');
    } catch (error) {
      // Storage can be unavailable (private mode, blocked cookies). The popup
      // still closes; it just cannot remember that it did.
    }

    const opener = this.#openedBy;
    this.#openedBy = null;
    if (wasOpen && opener && opener.isConnected && typeof opener.focus === 'function') {
      opener.focus();
    }
  }

  #isDismissed() {
    try {
      return Boolean(sessionStorage.getItem(MarketSuggestion.DISMISSED_KEY));
    } catch (error) {
      return false;
    }
  }

  // --- keyboard ------------------------------------------------------------

  #focusableElements() {
    return Array.from(
      this.querySelectorAll('a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):enabled')
    );
  }

  #onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.#focusableElements();
    if (!focusable.length) {
      event.preventDefault();
      this.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    // Focus escaped the dialog entirely (a click landed behind it): pull it
    // back rather than letting Tab walk the page underneath.
    if (active !== this && !this.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey) {
      if (active === first || active === this) {
        event.preventDefault();
        last.focus();
      }
      return;
    }

    if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #releaseKeyboard() {
    if (!this.#keydownHandler) return;
    document.removeEventListener('keydown', this.#keydownHandler);
    this.#keydownHandler = null;
  }
}

if (!customElements.get('market-suggestion')) {
  customElements.define('market-suggestion', MarketSuggestion);
}
